import {getLogger} from '@/shared/utils/logUtils'
import {OWN_ROOT} from '../core/storage'
import {BrowserRoot, FILE_TABLE_ROOT_KEY, readStoredRoot, storeRoot} from './fileBrowserRoot'

/**
 * Where the Files browser is, as one tree:
 *
 *   Home (the top of My Files) › a › b               a folder of My Files
 *   Home › Shared                                    the Shared virtual folder: the shared folders
 *   Home › Shared › Team data › runs                 a folder inside a shared folder
 *
 * Shared is a virtual folder pinned at the top of My Files (with the user's own pins); it holds no
 * files, only the shared folders, so it is a root of its own (`sharedIndex`) rather than a path.
 */
export interface BrowserLocation {
  root: BrowserRoot
  /**
   * The folder inside the root, in breadcrumb form: `['']` for its top, `['', 'a', 'b']` for `a/b`.
   * Always `['']` in the Shared virtual folder.
   */
  folderPath: string[]
}

/** The top of a root, in breadcrumb form. */
export const TOP_FOLDER: string[] = ['']

export const HOME: BrowserLocation = {root: OWN_ROOT, folderPath: TOP_FOLDER}

/**
 * localStorage key for the folder inside the root (breadcrumb form). The root is in
 * `fileTable-root` (see fileBrowserRoot.ts); the two together are the location. Both are cleared with
 * the user's other session keys.
 */
export const FILE_TABLE_FOLDER_PATH_KEY = 'fileTable-folderPath'

function readStoredFolderPath(): string[] {
  try {
    const raw = localStorage.getItem(FILE_TABLE_FOLDER_PATH_KEY)
    if (!raw) {
      return TOP_FOLDER
    }
    const parsed: unknown = JSON.parse(raw)
    if (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed[0] === '' &&
      parsed.slice(1).every((segment) => typeof segment === 'string' && segment !== '')
    ) {
      return parsed as string[]
    }
  } catch (error) {
    getLogger().error('Failed to parse stored folder path:', error)
  }
  return TOP_FOLDER
}

/** Whether a root other than My Files was stored (My Files leaves no key). */
function storedOtherRoot(): boolean {
  try {
    return localStorage.getItem(FILE_TABLE_ROOT_KEY) !== null
  } catch {
    return false
  }
}

/**
 * The remembered location, or Home when there is none or it does not parse. A remembered shared
 * folder comes back as it was stored: the browser checks it against the share list, and goes Home
 * if the share is gone.
 */
export function readStoredLocation(): BrowserLocation {
  const root = readStoredRoot()
  // The Shared virtual folder has no folders; a root that did not parse leaves its folder behind,
  // which was not one of My Files.
  if (root.kind === 'sharedIndex' || (root.kind === 'own' && storedOtherRoot())) {
    return {root, folderPath: TOP_FOLDER}
  }
  return {root, folderPath: readStoredFolderPath()}
}

export function storeLocation({root, folderPath}: BrowserLocation): void {
  storeRoot(root)
  try {
    localStorage.setItem(
      FILE_TABLE_FOLDER_PATH_KEY,
      JSON.stringify(root.kind === 'sharedIndex' ? TOP_FOLDER : folderPath)
    )
  } catch (error) {
    getLogger().error('Failed to store folder path:', error)
  }
}

/** Where the header's breadcrumb starts from. */
export interface LocationView {
  kind: BrowserRoot['kind']
  /** The shared folder on screen. */
  shareName?: string
  /** Whether there is a Home (My Files) to go to: not without a subscription. */
  showHome: boolean
}

/** What a breadcrumb does: go Home, to Shared, or to a folder of the path on screen, by position. */
export type CrumbTarget = {to: 'home'} | {to: 'shared'} | {to: 'folder'; index: number}

export interface Crumb {
  kind: 'home' | 'shared' | 'share' | 'folder'
  /** What it reads; Home is an icon. */
  label: string
  target: CrumbTarget
}

/**
 * The breadcrumb for a location: Home › a › b, Home › Shared, Home › Shared › share › a.
 *
 * Folders are addressed by position, not name: a name can repeat along a path (`a/x/b/x`). Home in
 * My Files is position 0 of the path on screen, so going there refreshes the listing as before.
 */
export function locationCrumbs(folderPath: readonly string[], view: LocationView): Crumb[] {
  const crumbs: Crumb[] = []
  if (view.kind === 'own') {
    crumbs.push({kind: 'home', label: '', target: {to: 'folder', index: 0}})
  } else if (view.showHome) {
    crumbs.push({kind: 'home', label: '', target: {to: 'home'}})
  }
  if (view.kind === 'sharedIndex') {
    crumbs.push({kind: 'shared', label: 'Shared', target: {to: 'shared'}})
    return crumbs
  }
  if (view.kind === 'share') {
    crumbs.push({kind: 'shared', label: 'Shared', target: {to: 'shared'}})
    crumbs.push({kind: 'share', label: view.shareName ?? '', target: {to: 'folder', index: 0}})
  }
  folderPath.slice(1).forEach((name, i) => {
    crumbs.push({kind: 'folder', label: name, target: {to: 'folder', index: i + 1}})
  })
  return crumbs
}

/** One toolbar action where the browser is: offered, or shown light grey with the reason. */
export interface ToolbarAction {
  enabled: boolean
  /** Why it is off, as its tooltip. */
  reason?: string
}

/**
 * The toolbar where the browser is. The buttons are the same everywhere (New Folder, Import Data,
 * Transfers, Refresh); an action that does not apply is shown disabled rather than taken away.
 */
export interface ToolbarActions {
  /** New Folder, into the folder on screen. */
  newFolder: ToolbarAction
  /** Upload Files and Upload Folder, into the folder on screen. */
  upload: ToolbarAction
  /** Imports from S3, HTTPS and FTP, which always land in My Files. */
  import: ToolbarAction
  /** Total usage: it is the user's own storage, so My Files only. */
  usage: boolean
}

export const TOOLBAR_REASONS = {
  shared: 'Not available in Shared',
  view: 'You can view this folder but not change it',
  moving: "Its owner is moving this folder. Changes are paused until it's done",
  importIntoShare: 'Imports go to My Files: not available in shared folders',
  picker: 'Not available while choosing a file',
} as const

const ON: ToolbarAction = {enabled: true}
const off = (reason: string): ToolbarAction => ({enabled: false, reason})

export const MY_FILES_TOOLBAR_ACTIONS: ToolbarActions = {
  newFolder: ON,
  upload: ON,
  import: ON,
  usage: true,
}

const allOff = (reason: string): ToolbarActions => ({
  newFolder: off(reason),
  upload: off(reason),
  import: off(reason),
  usage: false,
})

/**
 * The toolbar for where the browser is:
 * - My Files: everything.
 * - The Shared virtual folder: nothing to change (Refresh and Transfers still work).
 * - A shared folder: New Folder and uploads with Can edit, not imports; nothing with Can view, or
 *   while its owner moves it.
 * - The file-pick dialog: browse only (its header shows Refresh alone).
 */
export function toolbarActions({
  picker,
  root,
  moving = false,
}: {
  picker: boolean
  root: BrowserRoot
  /** The shared folder on screen is being moved by its owner: writes are paused. */
  moving?: boolean
}): ToolbarActions {
  if (picker) {
    return allOff(TOOLBAR_REASONS.picker)
  }
  switch (root.kind) {
    case 'own':
      return MY_FILES_TOOLBAR_ACTIONS
    case 'sharedIndex':
      return allOff(TOOLBAR_REASONS.shared)
    case 'share':
      if (root.access === 'view') {
        return allOff(TOOLBAR_REASONS.view)
      }
      if (moving) {
        return allOff(TOOLBAR_REASONS.moving)
      }
      return {newFolder: ON, upload: ON, import: off(TOOLBAR_REASONS.importIntoShare), usage: false}
  }
}
